import React, { useContext } from 'react';
import { Checkbox } from 'components/inputs/Checkbox';
import {
  setHasNoMatchOutcome,
  setDefaultResultValue,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/TruthTable/TruthTableService';
import { OutputCells, ActionsCell, IgnoreCell, Cell } from '../cells';
import { RuleOutputPath } from '@decisions/server/DecisionsFramework/Design/Flow';
import { ShowContextEditorLayerCallback } from 'domains/designers/TruthTable/TruthTable';
import { editDefaultValueItemTruthTable } from '@decisions/server/DecisionsFramework/Design/Flow/Service/RuleEditService';
import { startEditingDefaultValueItem } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';
import { newTruthTableSpecifiedResult } from '@decisions/server/DecisionsFramework/Design/Flow/Service/TruthTable/TruthTableSpecifiedResult';
import { SetValueEventObj } from '../cells/OutputCell';
import './DefaultRow.less';
import { Column } from '../columns/Column';
import { AddPhraseColumn } from '../columns';
import { RuleDesignerContext } from 'domains/designers/RuleDesigner/RuleDesignerContext';
import { getActionColumnsCount, getHeaderActions, hasActionsColumn } from '../TruthTableUtils';
import { HeaderActionValueCell } from '../cells/HeaderActionValueCell';
import { IRootGroupModel } from 'domains/designers/RuleDesigner/models/RootGroupModel';

export interface DefaultRowProps {
  rootModel: IRootGroupModel;
  /**
   * Determines how much columns "Has no match outcome" checkbox will take
   */
  hasNoMatchOutcomeColSpan: number;
  showContextEditorLayer: ShowContextEditorLayerCallback;
}

/**
 * A default row displays outputs which will be return if all rows return false.
 */
export function DefaultRow({ rootModel, hasNoMatchOutcomeColSpan, showContextEditorLayer }: DefaultRowProps) {
  const { designerSurface } = useContext(RuleDesignerContext);
  const defaultOutputs = rootModel.RuleDisplayData.DefaultResults;
  const outputColumnCount = defaultOutputs.length + getActionColumnsCount(rootModel, designerSurface);
  const hasNoMatchOutcome = rootModel.RuleDisplayData.HasNoMatchOutcome;
  //TODO alex: need to implement execution result for "Has No Match Outcome"
  const executionResult = null;
  const { isDebug } = useContext(RuleDesignerContext);
  const headerActions = getHeaderActions(rootModel);

  const defaultSetValue: (event: SetValueEventObj) => void = async ({ output, value }) => {
    if (output.Value !== value) {
      output.Value = value;
      setDefaultResultValue({
        ruleSessionId: rootModel.RuleSessionId,
        specResult: newTruthTableSpecifiedResult({
          RuleStepId: rootModel.Id,
          Name: output.DataDescriptionName,
          Value: value,
        }),
      });
    }
  };

  return (
    <tr className="tt-table__default-result-row">
      <Column colSpan={hasNoMatchOutcomeColSpan} className="tt-table__col-no-outcome">
        {!isDebug && (
          <Cell>
            <Checkbox
              className={'tt-table__btn-no-outcome'}
              label="Has No Match Outcome"
              checked={hasNoMatchOutcome}
              id={`truthtable-${rootModel.RuleSessionId}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHasNoMatchOutcome({
                  ruleSessionId: rootModel.RuleSessionId,
                  value: e.target.checked,
                });
              }}
            />
          </Cell>
        )}
      </Column>
      {!isDebug && <AddPhraseColumn />}
      {hasNoMatchOutcome && (
        <OutputCells
          ruleType={rootModel.RuleDisplayData.RuleType}
          rowModel={rootModel}
          executionResult={executionResult}
          outputs={defaultOutputs}
          getSingleResultContext={({ ruleSessionId }) =>
            editDefaultValueItemTruthTable({
              index: 0,
              ruleSessionId: ruleSessionId,
            })
          }
          getSingleResultInputMappingSession={({ ruleSessionId }) =>
            startEditingDefaultValueItem({
              index: 0,
              truthTableSessionId: ruleSessionId,
            })
          }
          getMultipleResultContext={({ index, ruleSessionId }) =>
            editDefaultValueItemTruthTable({
              index,
              ruleSessionId,
            })
          }
          getMultipleResultInputMappingSession={({ ruleSessionId, index }) =>
            startEditingDefaultValueItem({
              index,
              truthTableSessionId: ruleSessionId,
            })
          }
          setInlineValue={defaultSetValue}
          showContextEditorLayer={showContextEditorLayer}
        />
      )}
      {!hasNoMatchOutcome && Array(outputColumnCount).fill(IgnoreCell())}
      {hasActionsColumn(rootModel, designerSurface) && hasNoMatchOutcome && (
        <ActionsCell
          actionsModel={rootModel.ActionsModel}
          outputPath={RuleOutputPath.False}
          executionResult={executionResult}
        />
      )}
      {rootModel.allowActions &&
        hasNoMatchOutcome &&
        headerActions?.map(item => (
          <HeaderActionValueCell
            key={item.ActionId}
            sessionId={item.getSessionId()}
            actionModel={item}
            executionResult={executionResult}
          />
        ))}
    </tr>
  );
}
